import { Box, Divider, Drawer, Stack } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import { ToggleButtonGroupEnumConfigField } from '@/features/controller/configuration/components/drawers/ToggleButtonGroupEnumConfigField'
import type { BasicSerialConnectionNode } from '@/features/controller/configuration/models/controllerConfigTree'
import { SerialPort } from '@/features/controller/configuration/models/raspiProperties/types'
import type { ConfigDrawerProps } from '@/features/controller/configuration/types'
import { describeSerialPort } from '@/features/controller/configuration/utils/serialPorts'

type BasicSerialPortConfig = BasicSerialConnectionNode['config']

/** The port is the only setting: the basic devices detect their own baud rate and framing. */
export function BasicSerialPortSettingsDrawer({
  config,
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
}: Readonly<ConfigDrawerProps<BasicSerialPortConfig>>) {
  const { t } = useTranslation()
  const form = useForm<BasicSerialPortConfig>({
    defaultValues: config,
  })

  const hasChanges = form.watch('basicDevicesSerialPort') !== config.basicDevicesSerialPort

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <DrawerTitle
          title={t('controllers.configuration.device_connections.basic_serial.dialog.title')}
          onClose={onClose}
        />

        <Divider />

        <DrawerDescription
          description={t('controllers.configuration.device_connections.basic_serial.dialog.description')}
        />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Stack direction="column" padding={2} spacing={2}>
            <ToggleButtonGroupEnumConfigField
              enumLabels={{
                port1: describeSerialPort(SerialPort.port1, t),
                port2: describeSerialPort(SerialPort.port2, t),
              }}
              enumType={SerialPort}
              label={t('controllers.configuration.device_connections.serial.dialog.port')}
              name="basicDevicesSerialPort"
            />
          </Stack>
        </Box>

        <Divider />

        <DrawerActions
          hasChanges={hasChanges}
          onClose={onClose}
          onSave={(updatedConfig: BasicSerialPortConfig) => onConfigUpdated(updatedConfig)}
        />
      </FormProvider>
    </Drawer>
  )
}
